import { Button, Space, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Link } from 'react-router-dom'

import { readConsent, writeConsent } from './consent'
import { loadAnalytics, trackPageView } from './gtag'

/**
 * Asks before any analytics runs, and reports page views once it may.
 *
 * The banner and the loading live together because they are one decision: the
 * tag is injected by the same code that records the answer, so there is no path
 * where the script runs and the answer is missing.
 *
 * Declining is a button, not a close icon. A banner that only offers "accept"
 * and a cross is asking a question with one answer.
 */
export function CookieConsent() {
  const [consent, setConsent] = useState(() => readConsent())
  const location = useLocation()

  useEffect(() => {
    if (consent === 'granted') {
      loadAnalytics()
    }
  }, [consent])

  // Every route change is a page view that the tag cannot see for itself: none
  // of them reload the document.
  useEffect(() => {
    if (consent === 'granted') {
      trackPageView(location.pathname + location.search)
    }
  }, [consent, location.pathname, location.search])

  if (consent !== null) {
    return null
  }

  const answer = (choice: 'granted' | 'denied') => {
    writeConsent(choice)
    setConsent(choice)
  }

  return (
    <div
      role="dialog"
      aria-label="Analytics consent"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 1000,
        maxWidth: 720,
        margin: '0 auto',
        background: '#fff',
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
        padding: '16px 20px',
        display: 'flex',
        gap: 16,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <Typography.Text style={{ flex: 1, minWidth: 260 }}>
        We would like to measure which pages people read, using Google Analytics. It sets a cookie.
        Nothing about your profiles or your account is sent to it, and declining changes nothing
        about how the product works.{' '}
        <Link to="/privacy">What we collect</Link>
      </Typography.Text>
      <Space>
        <Button onClick={() => answer('denied')}>Decline</Button>
        <Button type="primary" onClick={() => answer('granted')}>
          Accept
        </Button>
      </Space>
    </div>
  )
}
